/**
 * Local scan history, privacy-preserving progress tracking (§4.11).
 *
 * We persist ONLY derived numbers (score, percentile, tier, sub-scores) in the
 * browser's localStorage, never a photo, never landmarks (§5.0). This is what
 * makes "re-scan free and watch it move" real on the web without any account or
 * server. The user can clear it anytime (Account page).
 */
import type { ScanResult, SubScoreId } from '@facet/engine';

const KEY = 'facet.history.v1';
const UID = 'facet.uid.v1';

export interface HistoryEntry {
  id: string;
  date: string; // ISO
  overall: number;
  percentile: number;
  tier: string;
  sub: Partial<Record<SubScoreId, number>>;
}

function safeGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}
function safeSet(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* private mode / disabled, history just won't persist */
  }
}

/** A stable anonymous id for this browser (used for optional API sync). */
export function getAnonId(): string {
  let id = safeGet(UID);
  if (!id) {
    id = crypto.randomUUID();
    safeSet(UID, id);
  }
  return id;
}

export function loadHistory(): HistoryEntry[] {
  const raw = safeGet(KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as HistoryEntry[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

const SUBS: SubScoreId[] = ['jawline', 'symmetry', 'harmony', 'eyes', 'nose', 'skin'];

export function addToHistory(result: ScanResult): HistoryEntry[] {
  const entry: HistoryEntry = {
    id: crypto.randomUUID(),
    date: new Date().toISOString(),
    overall: result.scores.overall.score,
    percentile: result.scores.overall.percentile,
    tier: result.tier.name,
    sub: Object.fromEntries(SUBS.map((s) => [s, result.scores[s].percentile])),
  };
  const next = [...loadHistory(), entry].slice(-200); // keep last 200 (matches writeHistory + account sync)
  safeSet(KEY, JSON.stringify(next));
  return next;
}

/** Overwrite the local history (used after an account sync merges in server data). */
export function writeHistory(entries: HistoryEntry[]): HistoryEntry[] {
  const next = [...entries]
    .sort((a, b) => a.date.localeCompare(b.date))
    .slice(-200); // cap local storage; the account keeps the full set server-side
  safeSet(KEY, JSON.stringify(next));
  return next;
}

export function clearHistory(): void {
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

/** Overall-score series for the trend chart (chronological). */
export function overallSeries(history: HistoryEntry[]): number[] {
  return history.map((h) => h.overall);
}
